'use client'

import { UserRoles } from '@/types/user'
import { createContext, ReactNode, useEffect, useMemo, useState } from 'react'
import { User, UserCompany } from '@/types/__domain__/identity'
import { readUser } from '@/services/rest/user'
import { COMPANY_ROLE_TYPE } from '@/types/__domain__/companies'

interface UserProviderProps {
    children: ReactNode
}

interface UserProviderValue {
    user?: User
    hasAnAdminRole: () => boolean
    getCompanyOfUser: () => UserCompany | null
    getCompanyCodeOfUser: () => string | null
    getComplianceCompanyOfUser: () => UserCompany | null
    isConsultant: boolean
    isLoading: boolean
}

export const UserContext = createContext<UserProviderValue>({
    hasAnAdminRole: () => false,
    getCompanyOfUser: () => null,
    getCompanyCodeOfUser: () => null,
    getComplianceCompanyOfUser: () => null,
    isConsultant: false,
    isLoading: false,
})

export const UserProvider = ({ children }: UserProviderProps) => {
    const [user, setUser] = useState<User | undefined>(undefined)
    const [isLoading, setIsLoading] = useState(false)

    const fetchUser = async () => {
        setIsLoading(true)
        try {
            const userResponse = await readUser()
            if (userResponse?.data && userResponse?.isSuccess) {
                setUser(userResponse.data)
            }
        } catch (error) {
            console.error('Error fetching user:', error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchUser()
    }, [])

    const hasAnAdminRole = () => {
        const adminRoles = [
            UserRoles.ADMIN_MASTER,
            UserRoles.ADMIN_BASIC,
            UserRoles.ADMIN_FINANCE,
            UserRoles.ADMIN_CONTRACTS,
            UserRoles.ADMIN_PRODUCERS,
            UserRoles.ADMIN_OPERATIONS,
            UserRoles.ADMIN_FINANCE_SUPPORT,
            UserRoles.ADMIN_OPERATIONS_SUPPORT,
        ]
        const someAdminMatchesUser = (userRole: UserRoles): boolean => {
            return adminRoles.some((adminRole: UserRoles) => adminRole === userRole)
        }
        return user?.userRoles?.some(someAdminMatchesUser) ?? false
    }

    const getCompanyOfUser = (): UserCompany | null => {
        return user?.activeCompany ?? null
    }

    const getCompanyCodeOfUser = (): string | null => {
        return getCompanyOfUser()?.companyCode ?? null
    }

    const getComplianceCompanyOfUser = (): UserCompany | null => {
        const userCompanies = user?.userCompanies ?? []
        const complianceCompany =
            userCompanies.find(
                (company) =>
                    company.companyRoles?.some((role) => role.companyRole === COMPANY_ROLE_TYPE.ComplianceCompany),
            ) || null
        return complianceCompany
    }

    const userProviderData: UserProviderValue = useMemo(
        () => ({
            user,
            getCompanyOfUser,
            getCompanyCodeOfUser,
            getComplianceCompanyOfUser,
            hasAnAdminRole,
            isConsultant: !!getComplianceCompanyOfUser(),
            isLoading,
        }),
        [{ user }],
    )

    return <UserContext.Provider value={userProviderData}>{children}</UserContext.Provider>
}
